// Marketing nav. Adapted from seidr-landing/components/Nav.tsx —
// same visual but wired to the Vite app's reality:
//   • Logo / nav links use real paths (/, /demo, /pricing) instead
//     of hash anchors that don't exist on this page yet
//   • "Log in" replaced with an Auth0-backed Sign in button — the
//     original linked to https://app.seidr.site/login (a placeholder
//     URL that doesn't exist)
//   • When signed in, the slot flips to "Open app →" linking to /app

import { useAuth0 } from "@auth0/auth0-react";
import { useMarketingLang } from "../i18n.jsx";

export default function Nav() {
  // useAuth0 throws if no Auth0Provider above. MarketingApp mounts
  // one when Auth0 is configured (VITE_AUTH0_DOMAIN set); when it
  // isn't (local dev with empty env), this returns null and the
  // Sign-in button renders disabled.
  let auth: ReturnType<typeof useAuth0> | null = null;
  try { auth = useAuth0(); } catch { /* no provider */ }
  const isAuthenticated = auth?.isAuthenticated;
  const loginWithRedirect = auth?.loginWithRedirect;
  const { t, language, setLanguage, initialLanguage } = useMarketingLang();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[rgba(10,14,26,0.72)] border-b-[0.5px] border-white/[0.06]">
      <div className="container-page flex items-center justify-between h-[68px]">
        <a
          href="/"
          className="text-[22px] font-medium tracking-tight text-teal hover:opacity-90 transition-opacity"
          style={{ letterSpacing: "0.04em" }}
        >
          SEIDR
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {/* The pricing section and its link were removed on 2026-10-14.
              Demo + Pricing scroll to their sections on the homepage
              instead of navigating to the standalone /demo and /pricing
              bundles. Those bundles are fixed-height (the demo is a
              map; the standalone pricing page was a short pricing-only
              view) and users hit them and felt stuck because they
              couldn't scroll back through the rest of the site without
              going Back. Anchor scroll keeps everyone on the long
              homepage where the full narrative flows naturally.
              When the user is already on the homepage these are pure
              in-page anchors; when on another page (/pricing,
              /app, /demo) the browser navigates to / first, then
              scrolls to the hash. */}
          <a
            href="/#try"
            className="text-[13px] text-white/70 hover:text-white transition-colors"
          >
            {t('nav.demo')}
          </a>
          {isAuthenticated ? (
            <a
              href="/app"
              className="text-[13px] text-white/70 hover:text-white transition-colors"
            >
              {t('nav.openApp')}
            </a>
          ) : (
            <button
              type="button"
              onClick={() => loginWithRedirect?.()}
              disabled={!loginWithRedirect}
              className="text-[13px] text-white/70 hover:text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('nav.signIn')}
            </button>
          )}
        </nav>

        <div className="flex items-center gap-4">
          {isAuthenticated ? (
            <a
              href="/app"
              className="inline-flex items-center px-4 py-2 rounded-full bg-teal text-bg-base text-[13px] font-medium hover:brightness-110 transition-all"
            >
              {t('nav.openAppArrow')}
            </a>
          ) : (
            <button
              type="button"
              // screen_hint=signup makes Auth0 land users on the Sign Up
              // form directly. Without it, "Sign up free" took them to
              // the Log in form with a small "Don't have an account?
              // Sign up" link they had to find — frustrating UX.
              onClick={() => loginWithRedirect?.({ authorizationParams: { screen_hint: 'signup' } })}
              disabled={!loginWithRedirect}
              className="inline-flex items-center px-4 py-2 rounded-full bg-teal text-bg-base text-[13px] font-medium hover:brightness-110 transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('nav.signUpFree')}
            </button>
          )}
          {initialLanguage !== 'en' && (
            <span className="inline-flex items-center gap-1 text-[12px] font-mono">
              <button
                type="button"
                onClick={() => setLanguage('it')}
                className={language === 'it' ? 'text-teal' : 'text-white/45 hover:text-white/70'}
              >
                IT
              </button>
              <span className="text-white/25">/</span>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={language === 'en' ? 'text-teal' : 'text-white/45 hover:text-white/70'}
              >
                EN
              </button>
            </span>
          )}
        </div>
      </div>
    </header>
  );
}
